/**
 * Reports Store
 * Alpine.js reactive store for reports state management
 */

import Alpine from 'alpinejs'
import type { ReportsStore, Report, ReportStatus } from '../types'
import {
  subscribeToReports,
  getReports,
  getReportsNearLocation,
  getPendingReports,
  updateReportStatus,
  deleteReport,
  getReportStats,
  getUniqueCountries,
  getCitiesForCountry,
  type ReportFilters,
} from '../services/reports'
import type { Unsubscribe } from 'firebase/firestore'

// ============================================================================
// Store Definition
// ============================================================================

export function createReportsStore(): ReportsStore & {
  // Extended methods
  init(filters?: ReportFilters): void
  fetchReports(): Promise<void>
  fetchNearby(lat: number, lng: number, radiusKm: number): Promise<void>
  fetchPending(): Promise<void>
  updateStatus(id: string, status: ReportStatus, notes?: string): Promise<void>
  remove(id: string): Promise<void>
  setFilter(key: string, value: string): void
  clearFilters(): void
  loadCountries(): Promise<void>
  loadCities(country: string): Promise<void>
  loadStats(): Promise<void>
  cleanup(): void
} {
  let unsubscribe: Unsubscribe | null = null

  return {
    items: [],
    pending: [],
    loading: false,
    error: null,
    filters: {
      status: 'all',
      animalType: 'all',
      country: 'all',
      city: 'all',
      dateRange: 'all',
    },
    countries: [],
    cities: [],
    stats: null,

    /**
     * Initialize store and subscribe to real-time updates
     * @param filters - Optional filters (e.g. { status: 'new' } for public pages)
     */
    init(filters?: ReportFilters): void {
      this.loading = true

      unsubscribe = subscribeToReports((reports: Report[]) => {
        this.items = reports
        this.loading = false
      }, filters ?? {})
    },

    /**
     * Fetch reports with current filters
     */
    async fetchReports(): Promise<void> {
      this.loading = true
      this.error = null

      try {
        const filters: ReportFilters = {}

        if (this.filters.status !== 'all') {
          filters.status = this.filters.status
        }
        if (this.filters.animalType !== 'all') {
          filters.animalType = this.filters.animalType
        }
        if (this.filters.country !== 'all') {
          filters.country = this.filters.country
        }
        if (this.filters.city !== 'all') {
          filters.city = this.filters.city
        }
        if (this.filters.dateRange !== 'all') {
          filters.dateRange = this.filters.dateRange as ReportFilters['dateRange']
        }

        const result = await getReports(filters)
        this.items = result.reports
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to fetch reports'
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch reports near a location
     */
    async fetchNearby(lat: number, lng: number, radiusKm: number): Promise<void> {
      this.loading = true
      this.error = null

      try {
        const reports = await getReportsNearLocation({ latitude: lat, longitude: lng }, radiusKm, {
          status: 'new', // Only show approved reports on public map
        })
        this.items = reports
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to fetch nearby reports'
      } finally {
        this.loading = false
      }
    },

    /**
     * Fetch pending reports (for admin)
     */
    async fetchPending(): Promise<void> {
      this.loading = true
      this.error = null

      try {
        this.pending = await getPendingReports()
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to fetch pending reports'
      } finally {
        this.loading = false
      }
    },

    /**
     * Update report status
     */
    async updateStatus(id: string, status: ReportStatus, notes?: string): Promise<void> {
      try {
        await updateReportStatus(id, status, notes)

        // Update local state
        const index = this.items.findIndex((r) => r.id === id)
        if (index !== -1) {
          this.items[index] = { ...this.items[index], status }
        }

        // Remove from pending if approved/rejected
        if (status !== 'pending') {
          this.pending = this.pending.filter((r) => r.id !== id)
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to update status'
        throw error
      }
    },

    /**
     * Delete a report
     */
    async remove(id: string): Promise<void> {
      try {
        await deleteReport(id)

        // Update local state
        this.items = this.items.filter((r) => r.id !== id)
        this.pending = this.pending.filter((r) => r.id !== id)
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to delete report'
        throw error
      }
    },

    /**
     * Set a filter value
     */
    setFilter(key: string, value: string): void {
      if (key in this.filters) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        ;(this.filters as any)[key] = value

        // Reset city when country changes
        if (key === 'country') {
          this.filters.city = 'all'
          if (value !== 'all') {
            void this.loadCities(value)
          } else {
            this.cities = []
          }
        }

        // Re-fetch reports with new filters
        void this.fetchReports()
      }
    },

    /**
     * Clear all filters
     */
    clearFilters(): void {
      this.filters = {
        status: 'all',
        animalType: 'all',
        country: 'all',
        city: 'all',
        dateRange: 'all',
      }
      this.cities = []
      void this.fetchReports()
    },

    /**
     * Load unique countries for filter
     */
    async loadCountries(): Promise<void> {
      try {
        this.countries = await getUniqueCountries()
      } catch (error) {
        console.error('Failed to load countries:', error)
      }
    },

    /**
     * Load cities for a country
     */
    async loadCities(country: string): Promise<void> {
      try {
        this.cities = await getCitiesForCountry(country)
      } catch (error) {
        console.error('Failed to load cities:', error)
      }
    },

    /**
     * Load report statistics
     */
    async loadStats(): Promise<void> {
      try {
        this.stats = await getReportStats()
      } catch (error) {
        console.error('Failed to load stats:', error)
      }
    },

    /**
     * Get filtered items
     */
    get filtered(): Report[] {
      return this.items.filter((report) => {
        if (this.filters.status !== 'all' && report.status !== this.filters.status) {
          return false
        }
        if (this.filters.animalType !== 'all' && report.animalType !== this.filters.animalType) {
          return false
        }
        // Use locationDetails for country/city filtering (old schema)
        const reportCountry = report.locationDetails?.country ?? ''
        const reportCity = report.locationDetails?.city ?? ''
        if (this.filters.country !== 'all' && reportCountry !== this.filters.country) {
          return false
        }
        if (this.filters.city !== 'all' && reportCity !== this.filters.city) {
          return false
        }
        return true
      })
    },

    /**
     * Get count of pending reports
     */
    get pendingCount(): number {
      return this.pending.length
    },

    /**
     * Cleanup subscriptions
     */
    cleanup(): void {
      if (unsubscribe) {
        unsubscribe()
        unsubscribe = null
      }
    },
  }
}

// ============================================================================
// Register Store
// ============================================================================

export function registerReportsStore(): void {
  Alpine.store('reports', createReportsStore())
}

// ============================================================================
// Type Declaration for Alpine
// ============================================================================

declare module 'alpinejs' {
  interface Stores {
    reports: ReturnType<typeof createReportsStore>
  }
}
